import { useState, useEffect } from 'react';

interface ScenarioConsultationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIndustry?: string;
  selectedMode?: string;
  selectedGoal?: string;
}

export default function ScenarioConsultationDrawer({ 
  isOpen, 
  onClose,
  selectedIndustry = '',
  selectedMode = '',
  selectedGoal = ''
}: ScenarioConsultationDrawerProps) {
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    company: '',
    industry: '',
    mode: '',
    goal: '',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // 自动填充选择器数据
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        industry: selectedIndustry,
        mode: selectedMode,
        goal: selectedGoal
      }));
    }
  }, [isOpen, selectedIndustry, selectedMode, selectedGoal]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.contact || !formData.company) {
      return;
    }

    if (formData.requirements.length > 500) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        contact: formData.contact,
        company: formData.company,
        industry: formData.industry,
        mode: formData.mode,
        goal: formData.goal,
        requirements: formData.requirements
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d6gohfqu8ojn6g516hu0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          contact: '',
          company: '',
          industry: selectedIndustry,
          mode: selectedMode,
          goal: selectedGoal,
          requirements: ''
        });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end">
      <div 
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      ></div>
      
      <div className="relative w-full max-w-md h-full bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-gray-900">咨询场景方案</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="px-6 py-6">
          <p className="text-sm text-gray-600 mb-6">
            请填写以下信息，我们的专业顾问将在1个工作日内与您联系，提供针对性的合规方案建议与起步报价。
          </p>

          <form id="scenario-consultation-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                姓名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="请输入您的姓名"
              />
            </div>

            <div>
              <label htmlFor="contact" className="block text-sm font-medium text-gray-700 mb-2">
                联系方式 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="contact"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="手机号或邮箱"
              />
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                公司名称 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="请输入公司名称"
              />
            </div>

            {/* 自动带入的选择器信息 */}
            {(formData.industry || formData.mode || formData.goal) && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <div className="flex items-start gap-2 mb-2">
                  <i className="ri-information-line text-teal-600 text-lg mt-0.5"></i>
                  <div className="text-sm text-teal-800 font-medium">已为您自动填充选择信息</div>
                </div>
                <div className="text-sm text-teal-700 space-y-1 ml-6">
                  {formData.industry && <div>• 行业：{formData.industry}</div>}
                  {formData.mode && <div>• 模式：{formData.mode}</div>}
                  {formData.goal && <div>• 目标：{formData.goal}</div>}
                </div>
              </div>
            )}

            <div>
              <label htmlFor="requirements" className="block text-sm font-medium text-gray-700 mb-2">
                具体需求备注
              </label>
              <textarea
                id="requirements"
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                placeholder="请简要描述您的业务情况与关注点（选填，限500字）"
              ></textarea>
              <div className="text-xs text-gray-400 mt-1 text-right">
                {formData.requirements.length}/500
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-green-600 text-lg mt-0.5"></i>
                <div className="text-sm text-green-800">
                  提交成功！我们将尽快与您联系。
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
                <i className="ri-error-warning-fill text-red-600 text-lg mt-0.5"></i>
                <div className="text-sm text-red-800">
                  提交失败，请稍后重试或直接联系我们。
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !formData.name || !formData.contact || !formData.company}
              className="w-full bg-teal-600 text-white py-3 rounded-lg font-medium hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? '提交中...' : '提交咨询'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 leading-relaxed">
              提交即表示您同意我们收集并处理您的信息以提供咨询服务。我们承诺保护您的隐私，不会将您的信息用于其他用途。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}